import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  useRidesMeService,
  useMemberMeService,
  RIDES_VERIFICATION_TYPE,
  RIDES_VERIFICATION_STATUS,
  type RidesMeSummaryView,
  type RidesRiderProfileView,
  type RidesDriverProfileView,
  type RidesVerificationView,
  type Member,
} from '@actschurch/ccms-sdk';
import { useEnrichedAuth } from '@/hooks/useEnrichedAuth';
import { ridesKeys, memberKeys } from './queryKeys';
import { isNotFoundError } from './copy';

/** Wrap a getter so a 404 resolves to null instead of an error. */
async function orNull<T>(fn: () => Promise<T>): Promise<T | null> {
  try {
    return await fn();
  } catch (e) {
    if (isNotFoundError(e)) return null;
    throw e;
  }
}

export function useMeSummary() {
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery<RidesMeSummaryView | null>({
    queryKey: ridesKeys.me.summary,
    queryFn: () => orNull(() => me.getSummary()),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

export function useRiderProfile() {
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery<RidesRiderProfileView | null>({
    queryKey: ridesKeys.me.profile,
    queryFn: () => orNull(() => me.getProfile()),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

export function useDriverProfile() {
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery<RidesDriverProfileView | null>({
    queryKey: ridesKeys.me.driver,
    queryFn: () => orNull(() => me.getDriver()),
    enabled: isAuthenticated,
    staleTime: 30_000,
  });
}

export function useMyVerifications() {
  const me = useRidesMeService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery<RidesVerificationView[]>({
    queryKey: ridesKeys.me.verification,
    queryFn: () => me.getVerifications(),
    enabled: isAuthenticated,
    staleTime: 15_000,
  });
}

export function useMemberMe() {
  const members = useMemberMeService();
  const { isAuthenticated } = useEnrichedAuth();
  return useQuery<Member | null>({
    queryKey: memberKeys.me,
    queryFn: () => members.getMe(),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

/** Invalidate everything "about me" after a profile / verification mutation. */
export function useInvalidateMe() {
  const qc = useQueryClient();
  return () => {
    qc.invalidateQueries({ queryKey: ridesKeys.me.all });
    qc.invalidateQueries({ queryKey: memberKeys.me });
  };
}

export function findVerification(list: RidesVerificationView[] | undefined, typeId: number): RidesVerificationView | undefined {
  return list?.find((v) => v.typeID === typeId);
}

export function isSubmitted(v: RidesVerificationView | null | undefined): boolean {
  return !!v && (v.statusID === RIDES_VERIFICATION_STATUS.Pending || v.statusID === RIDES_VERIFICATION_STATUS.Verified);
}

export function isVerified(v: RidesVerificationView | null | undefined): boolean {
  return !!v && v.statusID === RIDES_VERIFICATION_STATUS.Verified;
}

export const VERIFICATION_LABELS: Record<number, { title: string; sub: string }> = {
  [RIDES_VERIFICATION_TYPE.GovernmentId]: { title: 'Government ID', sub: 'SA ID number or passport' },
  [RIDES_VERIFICATION_TYPE.DriversLicence]: { title: "Driver's licence", sub: 'SA card or foreign licence' },
  [RIDES_VERIFICATION_TYPE.GuardianId]: { title: "Guardian's ID", sub: 'Parent or legal guardian' },
};
